import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreateProjectResponse,
  ProjectSettingsResponse,
  ProjectType,
  Project,
} from "../../models/Project.model";
import {
  createNewProject,
  getProjectSettings,
  updateProjectStatus as updateProjectStatusThunk,
  deleteProject,
  updateProjectComments,
} from "../thunks/projectThunks";
import { Resource } from "../../models/Resources.model";
import { CalendarEvent } from "../../models/CalendarEvent.model";
import {
  getProjectHeader,
  updateProjectHeader,
} from "../thunks/projectHeaderThunks";
import {
  getProjectTypes,
  createOrUpdateProjectType,
} from "../thunks/projectTypeThunks"; // הוספה חדשה
import { ProjectHeader } from "../../models/Project.model";
import { BaseStatusDTO } from "../../models/BaseStatus.model";
import { getLeadData } from "../thunks/leadThunks";

const convertCalendarEventToProject = (event: CalendarEvent): Project => {
  return {
    projectID: event.refDocId,
    contactID: 0, // יעודכן מהשרת
    projectName: event.displayText || event.contactName,
    projectStatus: 2, // סטטוס ברירת מחדל
    projectType: event.refDocType,
    createdDate: new Date(event.dateTime),
    resourceDiary: {
      resourceDiaryID: 0,
      resourceID: 0,
      startTime: new Date(event.dateTime),
      endTime: new Date(event.dateTime),
      remarks: event.displayText || "",
    },
  };
};

// פונקציות עזר ל-Session Storage - אופטימליות
const saveToSessionStorage = (key: string, data: any) => {
  try {
    const serialized = JSON.stringify(data);
    // בדיקה אם הנתונים באמת השתנו לפני שמירה
    if (sessionStorage.getItem(key) !== serialized) {
      sessionStorage.setItem(key, serialized);
    }
  } catch (error) {
    console.error(`Failed to save ${key} to session storage:`, error);
  }
};

const loadFromSessionStorage = (key: string, defaultValue: any = null) => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (error) {
    console.error(`Failed to load ${key} from session storage:`, error);
    return defaultValue;
  }
};

// שמירה מקובצת - יעילה יותר
const saveBatchToSessionStorage = (updates: Record<string, any>) => {
  try {
    Object.entries(updates).forEach(([key, value]) => {
      saveToSessionStorage(key, value);
    });
  } catch (error) {
    console.error("Failed to save batch to session storage:", error);
  }
};

interface ProjectState {
  createProjectLoading: boolean;
  createProjectError: string | null;
  createProjectSuccess: boolean;
  projectData: CreateProjectResponse | null;
  projectSettings: ProjectSettingsResponse | null;
  projectTypes: ProjectType[];
  projectStatuses: BaseStatusDTO[];
  resources: Resource[];
  projects: Project[];
  currentProject: Project | null;
  projectHeader: ProjectHeader | null;
  projectHeaderLoading: boolean;
  projectHeaderError: string | null;
  updateStatusLoading: boolean;
  updateStatusError: string | null;
  deleteProjectLoading: boolean;
  deleteProjectError: string | null;
}

const initialState: ProjectState = {
  createProjectLoading: false,
  createProjectError: null,
  createProjectSuccess: false,
  projectData: loadFromSessionStorage("projectData"),
  projectSettings: loadFromSessionStorage("projectSettings"),
  projectTypes: loadFromSessionStorage("projectTypes", []),
  projectStatuses: loadFromSessionStorage("projectStatuses", []),
  resources: loadFromSessionStorage("resources", []),
  projects: loadFromSessionStorage("projects", []),
  currentProject: loadFromSessionStorage("currentProject"),
  projectHeader: loadFromSessionStorage("projectHeader"),
  projectHeaderLoading: false,
  projectHeaderError: null,
  updateStatusLoading: false,
  updateStatusError: null,
  deleteProjectLoading: false,
  deleteProjectError: null,
};

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    setCurrentProjectFromEvent: (
      state,
      action: PayloadAction<CalendarEvent>,
    ) => {
      const project = convertCalendarEventToProject(action.payload);
      state.currentProject = project;
      saveToSessionStorage("currentProject", project);
    },
    clearProjectMessages: (state) => {
      state.createProjectError = null;
      state.createProjectSuccess = false;
    },
    resetCreateProjectState: (state) => {
      state.createProjectLoading = false;
      state.createProjectError = null;
      state.createProjectSuccess = false;
      state.projectData = null;
      sessionStorage.removeItem("projectData");
    },
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
      saveToSessionStorage("currentProject", action.payload);
    },
    updateProjectName: (state, action) => {
      if (state.projectHeader) {
        state.projectHeader.projectName = action.payload;
      }

      if (state.currentProject) {
        state.currentProject.projectName = action.payload;
      }

      saveBatchToSessionStorage({
        projectHeader: state.projectHeader,
        currentProject: state.currentProject,
      });
    },
    updateProjectStatus: (state, action) => {
      if (state.currentProject) {
        state.currentProject.projectStatus = action.payload;
        state.currentProject.projectStatusData = state.projectStatuses.find(
          (s) => s.baseStatusID === action.payload,
        );

        // עדכון ברשימת הפרויקטים
        const projectIndex = state.projects.findIndex(
          (p) => p.projectID === state.currentProject!.projectID,
        );
        if (projectIndex !== -1) {
          state.projects[projectIndex].projectStatus = action.payload;
          state.projects[projectIndex].projectStatusData =
            state.currentProject.projectStatusData;
        }

        // שמירה מקובצת
        saveBatchToSessionStorage({
          currentProject: state.currentProject,
          projects: state.projects,
        });
      }
    },
    updateProjectType: (state, action) => {
      if (state.currentProject) {
        state.currentProject.projectType = action.payload;
        state.currentProject.projectTypeData = state.projectTypes.find(
          (t) => t.projectTypeID === action.payload,
        );

        // עדכון ברשימת הפרויקטים
        const projectIndex = state.projects.findIndex(
          (p) => p.projectID === state.currentProject!.projectID,
        );
        if (projectIndex !== -1) {
          state.projects[projectIndex].projectType = action.payload;
          state.projects[projectIndex].projectTypeData =
            state.currentProject.projectTypeData;
        }

        // שמירה מקובצת
        saveBatchToSessionStorage({
          currentProject: state.currentProject,
          projects: state.projects,
        });
      }
    },
    addProject: (state, action) => {
      state.projects.push(action.payload);
      saveToSessionStorage("projects", state.projects);
    },
    updateProject: (state, action) => {
      const index = state.projects.findIndex(
        (p) => p.projectID === action.payload.projectID,
      );
      if (index !== -1) {
        state.projects[index] = action.payload;

        // עדכון הפרויקט הנוכחי אם זה אותו
        if (
          state.currentProject &&
          state.currentProject.projectID === action.payload.projectID
        ) {
          state.currentProject = action.payload;
        }

        saveBatchToSessionStorage({
          projects: state.projects,
          currentProject: state.currentProject,
        });
      }
    },
    removeProject: (state, action) => {
      state.projects = state.projects.filter(
        (p) => p.projectID !== action.payload,
      );

      // אם זה הפרויקט הנוכחי, נקה אותו
      if (
        state.currentProject &&
        state.currentProject.projectID === action.payload
      ) {
        state.currentProject = null;
        sessionStorage.removeItem("currentProject");
      }

      saveToSessionStorage("projects", state.projects);
    },
    clearProjectHeaderMessages: (state) => {
      state.projectHeaderError = null;
    },
    clearUpdateStatusMessages: (state) => {
      state.updateStatusError = null;
    },
    clearDeleteProjectMessages: (state) => {
      state.deleteProjectError = null;
    },
    setProjectHeader: (state, action) => {
      state.projectHeader = action.payload;
      saveToSessionStorage("projectHeader", action.payload);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(createNewProject.pending, (state) => {
        state.createProjectLoading = true;
        state.createProjectError = null;
        state.createProjectSuccess = false;
      })
      .addCase(createNewProject.fulfilled, (state, action) => {
        state.createProjectLoading = false;
        state.createProjectSuccess = true;
        state.projectData = action.payload;

        const newProject: Project = {
          projectID: action.payload.projectID,
          contactID: action.payload.contactID,
          projectName: action.meta.arg.projectName,
          projectStatus: action.meta.arg.projectStatus,
          projectType: action.meta.arg.projectType,
          createdDate: new Date(), // ✅ חזרנו ל-Date
          resourceDiary: action.meta.arg.projectResourceDiaryDTO
            ? {
                resourceDiaryID:
                  action.meta.arg.projectResourceDiaryDTO.resourceDiaryID,
                resourceID: action.meta.arg.projectResourceDiaryDTO.resourceID,
                startTime: new Date(
                  action.meta.arg.projectResourceDiaryDTO
                    .resourceDiaryStartTime,
                ),
                endTime: new Date(
                  action.meta.arg.projectResourceDiaryDTO.resourceDiaryEndTime,
                ),
                remarks:
                  action.meta.arg.projectResourceDiaryDTO.resourceDiaryRemarks,
              }
            : null,
        };

        state.projects.push(newProject);

        saveBatchToSessionStorage({
          projectData: action.payload,
          projects: state.projects,
        });
      })
      .addCase(createNewProject.rejected, (state, action) => {
        state.createProjectLoading = false;
        state.createProjectError = action.payload || "Failed to create project";
        state.createProjectSuccess = false;
      })
      .addCase(getProjectSettings.pending, (state) => {
        state.createProjectError = null;
      })
      .addCase(getProjectSettings.fulfilled, (state, action) => {
        state.projectSettings = action.payload;
        state.projectStatuses = action.payload.data.projectStatuses;
        state.projectTypes = action.payload.data.projectTypes;
        state.resources = action.payload.data.resources;

        // שמירה מקובצת
        saveBatchToSessionStorage({
          projectSettings: action.payload,
          projectStatuses: state.projectStatuses,
          projectTypes: state.projectTypes,
          resources: state.resources,
        });
      })
      .addCase(getProjectSettings.rejected, (state, action) => {
        state.createProjectError =
          action.payload || "Failed to get project settings";
      })
      .addCase(getLeadData.pending, (state) => {
        state.projectHeaderLoading = true;
        state.projectHeaderError = null;
      })
      .addCase(getProjectHeader.fulfilled, (state, action) => {
        state.projectHeaderLoading = false;
        const header = {
          ...action.payload.projectHeaderDTO,
          projectCreateDate:
            typeof action.payload.projectHeaderDTO.projectCreateDate ===
            "string"
              ? action.payload.projectHeaderDTO.projectCreateDate
              : new Date(
                  action.payload.projectHeaderDTO.projectCreateDate,
                ).toISOString(),
        };
        state.projectHeader = header;
        saveToSessionStorage("projectHeader", header);
      })
      .addCase(getLeadData.fulfilled, (state, action) => {
        const header: ProjectHeader = {
          projectID: action.payload.lead.projectID,
          projectCompanyID: 0,
          projectCreateDate: new Date().toISOString(),
          projectCreateBy: 0,
          projectName: action.payload.lead.projectName,
          projectStatus: action.payload.lead.projectStatusID,
          projectType: action.payload.lead.projectTypeID,
          projectTypeIcon: action.payload.lead.projectTypeIcon,
          appointmentsIconStatus: action.payload.lead.appointmentsIconStatus,
          tasksIconStatus: action.payload.lead.tasksIconStatus,
          communicationIconStatus: 0,
          projectAssignedTo: action.payload.lead.project_AssignedTo || 0,
        };
        state.projectHeader = header;
        saveToSessionStorage("projectHeader", header);
      })
      .addCase(getLeadData.rejected, (state, action) => {
        state.projectHeaderLoading = false;
        state.projectHeaderError =
          action.payload || "Failed to fetch project header";
      })
      .addCase(updateProjectHeader.pending, (state) => {
        state.projectHeaderLoading = true;
        state.projectHeaderError = null;
      })
      .addCase(updateProjectHeader.fulfilled, (state, action) => {
        state.projectHeaderLoading = false;
        const requestData = action.meta.arg;

        // עדכן את הכותרת המקומית
        if (state.projectHeader) {
          if (requestData.projectName !== undefined) {
            state.projectHeader.projectName = requestData.projectName;
          }
          if (requestData.projectType !== undefined) {
            state.projectHeader.projectType = requestData.projectType;
          }
          if (requestData.projectStatus !== undefined) {
            state.projectHeader.projectStatus = requestData.projectStatus;
          }
          if (requestData.assignedTo !== undefined) {
            state.projectHeader.projectAssignedTo = requestData.assignedTo;
          }
        }

        // עדכן את currentProject
        if (state.currentProject) {
          if (requestData.projectName !== undefined) {
            state.currentProject.projectName = requestData.projectName;
          }
          if (requestData.projectType !== undefined) {
            state.currentProject.projectType = requestData.projectType;
          }
          if (requestData.projectStatus !== undefined) {
            state.currentProject.projectStatus = requestData.projectStatus;
          }
        }

        // עדכן ברשימת הפרויקטים
        if (state.currentProject) {
          const projectIndex = state.projects.findIndex(
            (p) => p.projectID === state.currentProject!.projectID,
          );
          if (projectIndex !== -1) {
            if (requestData.projectName !== undefined) {
              state.projects[projectIndex].projectName =
                requestData.projectName;
            }
            if (requestData.projectType !== undefined) {
              state.projects[projectIndex].projectType =
                requestData.projectType;
            }
            if (requestData.projectStatus !== undefined) {
              state.projects[projectIndex].projectStatus =
                requestData.projectStatus;
            }
          }
        }

        saveBatchToSessionStorage({
          projectHeader: state.projectHeader,
          currentProject: state.currentProject,
          projects: state.projects,
        });
      })
      .addCase(updateProjectHeader.rejected, (state, action) => {
        state.projectHeaderLoading = false;
        state.projectHeaderError =
          action.payload || "Failed to update project header";
      })
      // הטיפול בסוגי פרויקטים - עדכון רשימה
      .addCase(getProjectTypes.fulfilled, (state, action) => {
        state.projectTypes = action.payload.projectTypes;
        saveToSessionStorage("projectTypes", action.payload.projectTypes);
      })
      .addCase(createOrUpdateProjectType.fulfilled, (state, action) => {
        const requestData = action.meta.arg;

        if (requestData.projectTypeID === 0) {
          // יצירה חדשה
          const newProjectType: ProjectType = {
            projectTypeID: action.payload.projectTypeID,
            projectTypeName: requestData.projectTypeName,
            projectTypeIsDefault: false,
            projectTypeImage: requestData.imageURL,
          };
          state.projectTypes.push(newProjectType);
        } else {
          // עדכון קיים
          const index = state.projectTypes.findIndex(
            (pt) => pt.projectTypeID === requestData.projectTypeID,
          );
          if (index !== -1) {
            state.projectTypes[index] = {
              ...state.projectTypes[index],
              projectTypeName: requestData.projectTypeName,
              projectTypeImage: requestData.imageURL,
            };
          }
        }
        saveToSessionStorage("projectTypes", state.projectTypes);
      })
      .addCase(updateProjectStatusThunk.pending, (state) => {
        state.updateStatusLoading = true;
        state.updateStatusError = null;
      })
      .addCase(updateProjectStatusThunk.fulfilled, (state, action) => {
        state.updateStatusLoading = false;
        const { projectID, newStatusID, statusIsDeal, statusIsLost } =
          action.payload;

        // עדכון הפרויקט הנוכחי
        if (
          state.currentProject &&
          state.currentProject.projectID === projectID
        ) {
          state.currentProject.projectStatus = newStatusID;
          if (state.currentProject.projectStatusData) {
            state.currentProject.projectStatusData.baseStatusIsDeal =
              statusIsDeal;
            state.currentProject.projectStatusData.baseStatusIsLost =
              statusIsLost;
          }
        }

        // עדכון ברשימת הפרויקטים
        const projectIndex = state.projects.findIndex(
          (p) => p.projectID === projectID,
        );
        if (projectIndex !== -1) {
          state.projects[projectIndex].projectStatus = newStatusID;
          if (state.projects[projectIndex].projectStatusData) {
            state.projects[projectIndex].projectStatusData!.baseStatusIsDeal =
              statusIsDeal;
            state.projects[projectIndex].projectStatusData!.baseStatusIsLost =
              statusIsLost;
          }
        }

        // עדכון כותרת הפרויקט
        if (
          state.projectHeader &&
          state.projectHeader.projectID === projectID
        ) {
          state.projectHeader.projectStatus = newStatusID;
        }

        saveBatchToSessionStorage({
          currentProject: state.currentProject,
          projects: state.projects,
          projectHeader: state.projectHeader,
        });
      })
      .addCase(updateProjectStatusThunk.rejected, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusError =
          action.payload || "Failed to update project status";
      })
      .addCase(deleteProject.pending, (state) => {
        state.deleteProjectLoading = true;
        state.deleteProjectError = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.deleteProjectLoading = false;
        const projectID = action.meta.arg.projectID;

        // הסרת הפרויקט מהרשימה
        state.projects = state.projects.filter(
          (p) => p.projectID !== projectID,
        );

        // אם זה הפרויקט הנוכחי, נקה אותו
        if (
          state.currentProject &&
          state.currentProject.projectID === projectID
        ) {
          state.currentProject = null;
          sessionStorage.removeItem("currentProject");
        }

        // אם זה כותרת הפרויקט הנוכחית, נקה אותה
        if (
          state.projectHeader &&
          state.projectHeader.projectID === projectID
        ) {
          state.projectHeader = null;
          sessionStorage.removeItem("projectHeader");
        }

        saveToSessionStorage("projects", state.projects);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.deleteProjectLoading = false;
        state.deleteProjectError = action.payload || "Failed to delete project";
      })
      .addCase(updateProjectComments.pending, (state) => {
        state.updateStatusLoading = true;
        state.updateStatusError = null;
      })
      .addCase(updateProjectComments.fulfilled, (state, _action) => {
        state.updateStatusLoading = false;
        // אם יש צורך לעדכן את ההערות במצב, ניתן להוסיף כאן
      })
      .addCase(updateProjectComments.rejected, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusError =
          action.payload || "Failed to update project comments";
      });
  },
});

export const {
  clearProjectMessages,
  resetCreateProjectState,
  setCurrentProject,
  updateProjectName,
  updateProjectStatus,
  updateProjectType,
  addProject,
  updateProject,
  removeProject,
  setCurrentProjectFromEvent,
  clearProjectHeaderMessages,
  clearUpdateStatusMessages,
  clearDeleteProjectMessages,
  setProjectHeader,
} = projectSlice.actions;

// Selectors
export const selectCurrentProject = (state: { project: ProjectState }) =>
  state.project.currentProject;
export const selectProjectTypes = (state: { project: ProjectState }) =>
  state.project.projectTypes;
export const selectProjectStatuses = (state: { project: ProjectState }) =>
  state.project.projectStatuses;
export const selectProjects = (state: { project: ProjectState }) =>
  state.project.projects;
export const selectProjectSettings = (state: { project: ProjectState }) =>
  state.project.projectSettings;
export const selectResources = (state: { project: ProjectState }) =>
  state.project.resources;
export const selectProjectHeader = (state: { project: ProjectState }) =>
  state.project.projectHeader;
export const selectProjectHeaderLoading = (state: { project: ProjectState }) =>
  state.project.projectHeaderLoading;
export const selectProjectHeaderError = (state: { project: ProjectState }) =>
  state.project.projectHeaderError;

// בדיקה אם ההגדרות נטענו
export const selectProjectSettingsLoaded = (state: { project: ProjectState }) =>
  state.project.projectTypes.length > 0 &&
  state.project.projectStatuses.length > 0 &&
  state.project.resources.length > 0;

// בדיקה אם ההגדרות בתהליך טעינה
export const selectProjectSettingsLoading = (state: {
  project: ProjectState;
}) => state.project.createProjectLoading;

// סלקטור משולב לבדיקת מצב ההגדרות
export const selectProjectSettingsStatus = (state: {
  project: ProjectState;
}) => ({
  loaded:
    state.project.projectTypes.length > 0 &&
    state.project.projectStatuses.length > 0 &&
    state.project.resources.length > 0,
  loading: state.project.createProjectLoading,
  error: state.project.createProjectError,
});

// ייצוא ה-thunk לשימוש בקומפוננטים
export { updateProjectStatusThunk };

export default projectSlice.reducer;
